import { defineStore } from "pinia";
import ConversationsSlice, { ConversationItem } from "@/api/conversations";
import type { MatchItem, MessageNewApi } from "../api/conversations.ts";
import { fetchLikes, type LikeItem, type LikesResponse } from "../api/under-nav/likes";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
dayjs.extend(utc);
dayjs.extend(timezone);

const MAX_MESSAGES_PER_CHAT = 150;
const TEMP_MESSAGE_ID_THRESHOLD = 1_000_000_000_000;

function getMessageIdNumber(message: { id?: unknown }): number | null {
    const id = message.id;
    if (typeof id === "number") return id;
    if (typeof id === "string" && id.trim() !== "") {
        const numericId = Number(id);
        return Number.isFinite(numericId) ? numericId : null;
    }
    return null;
}

function withNormalizedMessageId<T extends Partial<MessageNewApi>>(message: T): T {
    const numericId = getMessageIdNumber(message);
    return numericId === null ? message : { ...message, id: numericId };
}

function hasServerId(message: MessageNewApi): message is MessageNewApi & { id: number } {
    const id = getMessageIdNumber(message);
    return id !== null && id > 0 && id < TEMP_MESSAGE_ID_THRESHOLD;
}

function isPendingLocalMessage(message: MessageNewApi): boolean {
    const id = getMessageIdNumber(message);
    return id === null || id >= TEMP_MESSAGE_ID_THRESHOLD;
}

function normalizeComparableText(value: string | null | undefined): string {
    return (value ?? "").trim();
}

function areSamePendingMessage(pending: MessageNewApi, incoming: MessageNewApi): boolean {
    if (!isPendingLocalMessage(pending) || !hasServerId(incoming)) return false;
    if (String(pending.sender_id) !== String(incoming.sender_id)) return false;
    if (pending.type !== incoming.type) return false;
    if ((pending.contact_type ?? null) !== (incoming.contact_type ?? null)) return false;
    if ((pending.gift ?? null) !== (incoming.gift ?? null)) return false;

    const pendingBody = normalizeComparableText(pending.message);
    const incomingBody = normalizeComparableText(incoming.message);
    const pendingMedia = pending.file_url || pending.fid || pending.file_id || pending.file_fid || pending.media_fid;
    const incomingMedia = incoming.file_url || incoming.fid || incoming.file_id || incoming.file_fid || incoming.media_fid;
    const hasMediaIdentity = !!pendingMedia || !!incomingMedia;

    if (hasMediaIdentity) {
        if (pendingMedia !== incomingMedia) return false;
    } else if (pendingBody !== incomingBody) {
        return false;
    }

    return true;
}

export const useConversationsStore = defineStore("conversations", {
    state: () => ({
        conversations: [] as ConversationItem[],
        messages: {} as Record<number, MessageNewApi[]>,
        matches: [] as MatchItem[],
        matchCount: 0,
        lastLikesFetch: 0,
        likesTTL: 30_000,
        currentChat: null as ConversationItem | null,
        loading: false,
        lastFetch: 0,
        cacheTTL: 60_000,
        likesCount: [] as LikeItem[],
        previousLikesCount: 0,
        newLikesCount: 0,
        hasUnread: false,
    }),

    getters: {
        unreadExists: (state) => state.conversations.some((conv) => conv.unread_count > 0),
    },

    actions: {
        async fetchConversations(force = false) {
            const now = Date.now();
            if (!force && this.conversations.length > 0 && now - this.lastFetch < this.cacheTTL) {
                return this.conversations;
            }
            this.loading = this.conversations.length === 0;
            try {
                const response = await ConversationsSlice.GetConversations();
                const data = response?.data ?? { conversations: [], match_count: 0, match: [] };
                let newConversations = data.conversations ?? [];

                newConversations = newConversations.filter(
                    (chat: ConversationItem) => chat.last_message !== null || chat.user.id === 'news'
                );

                newConversations = newConversations.map((chat: ConversationItem) => ({
                    ...chat,
                    last_message: chat.last_message
                        ? {
                            ...chat.last_message,
                            timestamp: dayjs
                                .utc(chat.last_message.timestamp)
                                .add(3, "hour")
                                .toISOString(),
                        }
                        : chat.last_message,
                }));

                this.conversations = newConversations;
                this.matches = data.match ?? [];
                this.matchCount = data.match_count ?? 0;
                this.lastFetch = now;
                this.hasUnread = this.unreadExists;
            } finally {
                this.loading = false;
            }
        },

        async fetchConversationMessages(conversationId: number, page: number = 1): Promise<MessageNewApi[]> {
            this.loading = this.conversations.length === 0;
            try {
                const newMessages = await ConversationsSlice.GetConversationById(conversationId, page);
                if (!this.messages[conversationId]) {
                    this.messages[conversationId] = [];
                }
                const visibleMessages = newMessages.filter(message => !message.is_deleted);
                if (page === 1) {
                    this.messages[conversationId] = visibleMessages.slice(-MAX_MESSAGES_PER_CHAT);
                } else {
                    const combined = [...visibleMessages, ...this.messages[conversationId]];
                    this.messages[conversationId] = combined.slice(-MAX_MESSAGES_PER_CHAT);
                }
                return newMessages;
            } finally {
                this.loading = false;
            }
        },

        addMessage(conversationId: number, msg: MessageNewApi) {
            if (!this.messages[conversationId]) this.messages[conversationId] = [];
            msg = withNormalizedMessageId(msg);

            if (hasServerId(msg)) {
                const incomingId = getMessageIdNumber(msg);
                const existIdx = this.messages[conversationId].findIndex(m => getMessageIdNumber(m) === incomingId);
                if (existIdx !== -1) {
                    this.messages[conversationId][existIdx] = {
                        ...this.messages[conversationId][existIdx],
                        ...msg,
                    };
                    return;
                }

                const pendingIdx = this.messages[conversationId].findIndex(m => areSamePendingMessage(m, msg));
                if (pendingIdx !== -1) {
                    this.messages[conversationId][pendingIdx] = {
                        ...this.messages[conversationId][pendingIdx],
                        ...msg,
                    };
                    return;
                }
            }

            this.messages[conversationId].push(msg);
            if (this.messages[conversationId].length > MAX_MESSAGES_PER_CHAT) {
                this.messages[conversationId] = this.messages[conversationId].slice(-MAX_MESSAGES_PER_CHAT);
            }
        },

        async removeConversation(id: number) {
            try {
                await ConversationsSlice.DeleteConversation(id);
                this.conversations = this.conversations.filter(c => c.chat_id !== id);
                delete this.messages[id];
            } catch (err) {
                console.error("Ошибка при удалении диалога:", err);
            }
        },

        async pinConversation(id: number) {
            try {
                await ConversationsSlice.PinConversation(id);
                const conv = this.conversations.find(c => c.chat_id === id);
                if (conv) conv.is_pinned = !conv.is_pinned;
            } catch (err) {
                console.error("Ошибка при изменении закрепа:", err);
            }
        },

        async markMessagesAsRead(conversationId: number) {
            try {
                await ConversationsSlice.ReadMessages(conversationId);
                const msgs = this.messages[conversationId];
                if (msgs) {
                    msgs.forEach((msg) => { msg.is_read = true; });
                }
            } catch (e) {
                console.error("Ошибка при отметке сообщений как прочитанных", e);
            }
        },

        async fetchLikes(filter?: string, force = false): Promise<LikesResponse> {
            const now = Date.now();
            if (!force && this.likesCount.length > 0 && now - this.lastLikesFetch < this.likesTTL) {
                return { items: this.likesCount };
            }
            try {
                const res = await fetchLikes(filter);
                const newLikes = res.items || [];
                const prevCount = this.likesCount.length;
                const currentCount = newLikes.length;
                this.previousLikesCount = prevCount;
                this.likesCount = newLikes;
                this.newLikesCount = Math.max(currentCount - prevCount, 0);
                this.lastLikesFetch = now;
                return { items: this.likesCount };
            } catch (e) {
                console.error("Ошибка при получении лайков:", e);
                throw e;
            }
        },

        updateMessageReactions(conversationId: number, messageId: number, reactions: import('@/api/conversations').ReactionCount[], myReaction: string) {
            const msgs = this.messages[conversationId];
            if (!msgs) return;
            const idx = msgs.findIndex(m => m.id === messageId);
            if (idx === -1) return;
            msgs[idx] = { ...msgs[idx], reactions: [...reactions], my_reaction: myReaction };
        },

        updateMessage(conversationId: number, messageId: number, patch: Partial<MessageNewApi>) {
            const msgs = this.messages[conversationId];
            if (!msgs) return;
            patch = withNormalizedMessageId(patch);
            const idx = msgs.findIndex(m => getMessageIdNumber(m) === messageId);
            if (idx === -1) return;

            const patchId = getMessageIdNumber(patch);
            if (patchId !== null && patchId !== messageId) {
                const existingServerIdx = msgs.findIndex(m => getMessageIdNumber(m) === patchId);
                if (existingServerIdx !== -1) {
                    const mergedMessage = { ...msgs[idx], ...msgs[existingServerIdx], ...patch };
                    this.messages[conversationId] = msgs
                        .map((message, messageIndex) => {
                            if (messageIndex === existingServerIdx) return mergedMessage;
                            if (message.reply_to?.id !== messageId) return message;
                            return {
                                ...message,
                                reply_to: {
                                    ...message.reply_to,
                                    id: patchId,
                                    message: mergedMessage.message,
                                    type: mergedMessage.type,
                                    file_url: mergedMessage.file_url ?? message.reply_to.file_url ?? null,
                                    fid: mergedMessage.fid ?? message.reply_to.fid ?? null,
                                    file_id: mergedMessage.file_id ?? message.reply_to.file_id ?? null,
                                    file_fid: mergedMessage.file_fid ?? message.reply_to.file_fid ?? null,
                                    media_fid: mergedMessage.media_fid ?? message.reply_to.media_fid ?? null,
                                    is_deleted: !!mergedMessage.is_deleted,
                                },
                            };
                        })
                        .filter((_, messageIndex) => messageIndex !== idx);
                    return;
                }
            }

            const updatedMessage = { ...msgs[idx], ...patch };
            msgs[idx] = updatedMessage;
            this.messages[conversationId] = msgs.map((message, messageIndex) => {
                if (messageIndex === idx) return updatedMessage;
                if (message.reply_to?.id !== messageId) return message;
                return {
                    ...message,
                    reply_to: {
                        ...message.reply_to,
                        message: updatedMessage.message,
                        type: updatedMessage.type,
                        file_url: updatedMessage.file_url ?? message.reply_to.file_url ?? null,
                        fid: updatedMessage.fid ?? message.reply_to.fid ?? null,
                        file_id: updatedMessage.file_id ?? message.reply_to.file_id ?? null,
                        file_fid: updatedMessage.file_fid ?? message.reply_to.file_fid ?? null,
                        media_fid: updatedMessage.media_fid ?? message.reply_to.media_fid ?? null,
                        is_deleted: !!updatedMessage.is_deleted,
                    },
                };
            });
        },

        removeMessage(conversationId: number, messageId: number) {
            const msgs = this.messages[conversationId];
            if (!msgs) return;
            this.messages[conversationId] = msgs.filter(m => m.id !== messageId);
        },

        removeMatch(id: string) {
            this.matches = this.matches.filter(match => match.id !== id);
            this.matchCount = Math.max(0, this.matchCount - 1);
        },

        resetStore() {
            this.$reset();
        },
    },

    persist: {
        key: 'conversations-store',
        storage: sessionStorage,
        pick: ['conversations', 'matches', 'matchCount', 'hasUnread', 'likesCount', 'previousLikesCount', 'newLikesCount'],
    },
});
